import * as DropdownMenu from '@wandb/weave/components/DropdownMenu';
import {IconChevronNext, IconDownload} from '@wandb/weave/components/Icon';
import React, {useCallback, useState} from 'react';

import {DD_PRIVACY_ALLOW} from '../../../util/datadog/privacy';
import {ImageExportType} from '../../../util/panelExport';
import {isExportable, isExportableAs} from '../../../util/panels';
import {type getPanelSpec} from '../../../util/panelTypes';
import {usePanelContext} from '../PanelContextProvider';
import {ExportType} from '../types';

type PanelExportMenuProps = {
  ref: React.RefObject<HTMLElement>;
  panelSpec: ReturnType<typeof getPanelSpec>;
  setExportType(type: ExportType): void;
  setImageExportType(imageType: ImageExportType): void;
};

export const PanelExportMenu = React.forwardRef<
  HTMLElement,
  PanelExportMenuProps
>(({panelSpec, setExportType, setImageExportType}, _ref) => {
  const {trackPanelControls} = usePanelContext();
  const onExportImageClick = useCallback(
    (imageType: ImageExportType) => {
      setExportType(ExportType.Image);
      setImageExportType(imageType);
      trackPanelControls({
        action: 'Panel exported',
        exportType: imageType,
      });
    },
    [setExportType, setImageExportType, trackPanelControls]
  );

  const onExportClick = useCallback(
    (exportType: ExportType) => {
      setExportType(exportType);
      trackPanelControls({
        action: 'Panel exported',
        exportType: 'csv',
      });
    },
    [setExportType, trackPanelControls]
  );

  const [isOpen, setIsOpen] = useState(false);

  if (!isExportable(panelSpec)) {
    return null;
  }

  return (
    <DropdownMenu.Sub open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenu.SubTrigger
        data-dd-action-name="Download panel"
        data-dd-privacy={DD_PRIVACY_ALLOW}>
        <div className="flex w-full items-center gap-10">
          <IconDownload />
          Download
          <IconChevronNext className="ml-auto text-moon-450" />
        </div>
      </DropdownMenu.SubTrigger>
      <DropdownMenu.Portal>
        {/* zIndex is needed so the dropdown is above the panel in reports */}
        <DropdownMenu.SubContent style={{zIndex: 1}}>
          {isExportableAs(panelSpec, 'csv') && (
            <DropdownMenu.Item
              title="CSV"
              data-dd-action-name="Download panel as csv"
              data-dd-privacy={DD_PRIVACY_ALLOW}
              onSelect={() => onExportClick(ExportType.CSV)}>
              CSV
            </DropdownMenu.Item>
          )}
          {isExportableAs(panelSpec, 'image') && (
            <>
              <DropdownMenu.Item
                data-dd-action-name="Download panel as png"
                data-dd-privacy={DD_PRIVACY_ALLOW}
                onSelect={() => onExportImageClick(ImageExportType.PNG)}>
                PNG
              </DropdownMenu.Item>
              <DropdownMenu.Item
                data-dd-action-name="Download panel as pdf"
                data-dd-privacy={DD_PRIVACY_ALLOW}
                onSelect={() => onExportImageClick(ImageExportType.PDF)}>
                PDF
              </DropdownMenu.Item>
              <DropdownMenu.Item
                data-dd-action-name="Download panel as svg"
                data-dd-privacy={DD_PRIVACY_ALLOW}
                onSelect={() => onExportImageClick(ImageExportType.SVG)}>
                SVG
              </DropdownMenu.Item>
            </>
          )}
        </DropdownMenu.SubContent>
      </DropdownMenu.Portal>
    </DropdownMenu.Sub>
  );
});
